import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useSession } from "@/composables/useSession";
import { notify } from "notiwind";
import { useNavStore } from "@/stores/navigation";

import { useIsHrEnabled } from "@/composables/isHrEnabled";
import { useCompanyStore } from "@/stores/company";
import { useEmployeeStore } from "@/stores/employees";
import { syncRouteContext, syncHrRouteContext } from "@/composables/syncRouteContext";
import { dashboardRoute } from "@/utils/appRoutes";

/**
 * Shared HR guard: ensures `:employeeId` resolves to an employee in the
 * currently selected company, then applies a per-subpage rule.
 * @param {(employee: object, to: import('vue-router').RouteLocationNormalized) => (import('vue-router').RouteLocationRaw | true)} rule
 */
function hrEmployeeGuard(rule) {
  return async (to) => {
    const result = await syncHrRouteContext(to);
    if (result.redirect === "notFound") {
      return { name: "NotFound" };
    }
    if (!result.employee) {
      return { name: "NotFound" };
    }
    return rule(result.employee, to);
  };
}

// Treat both empty-string and null codes as "codeless" so employees whose
// `code` is `null` in the DB are routed to onboarding consistently with the
// onboarding overview GraphQL where (empty or null code, not archived).
function isCodeless(employee) {
  return (employee.code === "" || employee.code == null) && !employee.isArchived;
}

const enforceHasCode = hrEmployeeGuard((employee, to) => {
  if (isCodeless(employee)) {
    return { name: "HROnboarding", params: { ...to.params } };
  }
  return true;
});

const enforceNoCode = hrEmployeeGuard((employee, to) => {
  if (!isCodeless(employee)) {
    return { name: "HRProfile", params: { ...to.params } };
  }
  return true;
});

const resolveEmployee = hrEmployeeGuard((employee, to) => {
  if (isCodeless(employee)) {
    return { name: "HROnboarding", params: { ...to.params } };
  }
  return { name: "HRProfile", params: { ...to.params } };
});

const isOnline = import.meta.env.VITE_APP_ONLINE;
//const authExpired = computed(() => useAuthStore().expired);

const routes = [
  {
    path: "/dev",
    name: "Developer",
    meta: { requiresAuth: true },
    component: () => import("@/layouts/base.vue"),
    children: [
      {
        path: "drawer",
        name: "Drawer",
        component: () => import("@/components/dev/Drawer.vue"),
      },
      {
        path: "dialog",
        name: "Dialog",
        component: () => import("@/components/dev/Dialog.vue"),
      },
      {
        path: "colors",
        name: "Colors",
        component: () => import("@/components/dev/Colors.vue"),
      },
    ],
  },
  {
    path: "/",
    name: "Home",
    meta: { requiresAuth: true },
    beforeEnter: async (to, from) => {
      // const { code, message } = await useAuthStore().checkauth();
      // if (code === "ERROR") {
      //   // Do not display auth problem from root or from signout
      //   if (
      //     from.path !== "/" &&
      //     from.path !== "/logout/no" &&
      //     from.path !== "/logout/yes" &&
      //     from.name !== "SignOutCallback"
      //   ) {
      //     console.log("Auth Error");
      //     console.dir(from, { depth: null });
      //     // redirect to user profile
      //     notify(
      //       {
      //         type: "error",
      //         title: "Authentication Issue",
      //         text: "Possible reasons: No permissions. Login Session expired. Account disabled.",
      //       },
      //       10000
      //     );
      //   }
      //   return false;
      //   //return { name: "RequestSignIn" };
      // }
      const companyStore = useCompanyStore();
      if (!companyStore.selected?.slug) {
        await companyStore.getData(true);
      }
      const slug = companyStore.selected?.slug;
      if (slug) {
        return { name: "Dashboard", params: { companySlug: slug } };
      }
      return { name: "NotFound" };
    },
  },
  {
    path: "/dashboard",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "DashboardEntry",
        beforeEnter: async () => {
          const companyStore = useCompanyStore();
          if (!companyStore.selected?.slug) {
            await companyStore.getData(true);
          }
          const slug = companyStore.selected?.slug;
          if (slug) {
            return { path: `/dashboard/${encodeURIComponent(slug)}`, replace: true };
          }
          return { name: "NotFound" };
        },
      },
      {
        name: "Dashboard",
        path: ":companySlug",
        meta: { companyScoped: true },
        component: () => import("@/pages/dashboard.vue"),
        beforeEnter: () => {
          if (useSession().can.profileOnly) {
            return { name: "HrEntry" };
          }
        },
      },
    ],
  },
  {
    path: "/signin",
    component: () => import("@/layouts/pre.vue"),
    children: [
      {
        name: "RequestSignIn",
        path: "",
        component: () => import("@/components/auth/RequestSignIn.vue"),
      },
      // {
      //   name: "AutoSignIn",
      //   path: ":email/:token",
      //   component: () => import("@/components/auth/AutoSignIn.vue"),
      // },
    ],
  },
  {
    path: "/callback",
    component: () => import("@/layouts/pre.vue"),
    children: [
      {
        name: "AuthCallback",
        path: "",
        component: () => import("@/components/auth/Callback.vue"),
      },
    ],
  },
  // {
  //   path: "/silent-renew",
  //   component: () => import("@/layouts/pre.vue"),
  //   children: [
  //     {
  //       name: "AuthSilentRenew",
  //       path: "",
  //       component: () => import("@/components/auth/Renew.vue"),
  //     },
  //   ],
  // },
  {
    path: "/logout",
    component: () => import("@/layouts/pre.vue"),
    children: [
      {
        name: "RequestSignOut",
        path: ":silently?",
        props: true,
        component: () => import("@/components/auth/SignOut.vue"),
      },
    ],
  },
  {
    path: "/signout-callback",
    component: () => import("@/layouts/pre.vue"),
    children: [
      {
        name: "SignOutCallback",
        path: ":silently?",
        props: true,
        component: () => import("@/components/auth/SignOutCallback.vue"),
      },
    ],
  },
  {
    path: "/clear-cache",
    component: () => import("../layouts/pre.vue"),
    children: [
      {
        name: "ClearCache",
        path: "",
        component: () => import("@/pages/clearcache.vue"),
      },
    ],
  },
  {
    path: "/open-manager",
    meta: { requiresAuth: true },
    component: () => import("../layouts/base.vue"),
    children: [
      {
        name: "OpenManager",
        path: "",
        component: () => import("@/pages/openmanager.vue"),
        beforeEnter: () => {
          if (useSession().account.value?.role?.canAccessKeystone === true) {
            return true;
          }
          return {name: 'RequestSignIn'};
        },
      },
    ],
  },
  {
    path: "/hr",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "HrEntry",
        beforeEnter: async () => {
          const companyStore = useCompanyStore();
          if (!companyStore.selected?.slug) {
            await companyStore.getData(true);
          }
          const slug = companyStore.selected?.slug;
          if (slug) {
            return { path: `/hr/${encodeURIComponent(slug)}`, replace: true };
          }
          return { name: "NotFound" };
        },
      },
      {
        path: ":companySlug",
        meta: { companyScoped: true },
        component: () => import("@/pages/hr.vue"),
        children: [
          {
            path: "",
            name: "HrCompanyIndex",
            beforeEnter: async (to) => {
              const employeeStore = useEmployeeStore();
              if (employeeStore.allEmployees.length === 0 && !employeeStore.loading) {
                await employeeStore.getData();
              }
              if (employeeStore.companyEmployees.length === 0) {
                return true;
              }
              const currentSelected = employeeStore.lastSelected;
              const currentCompanyId = useCompanyStore().selectedCompanyId;
              const useCurrent =
                currentSelected?.id &&
                currentSelected?.company?.id === currentCompanyId &&
                employeeStore.companyEmployees.some((e) => e.id === currentSelected.id);
              let pick = useCurrent ? currentSelected : null;
              if (!pick) {
                const firstLetter = employeeStore.employeesLetters[0];
                const byLetter = firstLetter ? employeeStore.employeesByLetter(firstLetter) : [];
                pick = byLetter[0] || employeeStore.companyEmployees[0] || null;
              }
              if (!pick) {
                return true;
              }
              return {
                name: "HrEmployeeIndex",
                params: { companySlug: to.params.companySlug, employeeId: pick.id },
                replace: true,
              };
            },
          },
          {
            path: ":employeeId",
            children: [
              {
                path: "",
                name: "HrEmployeeIndex",
                beforeEnter: resolveEmployee,
              },
              {
                path: "profile",
                name: "HRProfile",
                component: () => import("@/components/hr/profile-v2/index.vue"),
                beforeEnter: enforceHasCode,
              },
              {
                path: "salaries",
                name: "HRSalaries",
                component: () => import("@/components/hr/salary/index.vue"),
                beforeEnter: enforceHasCode,
              },
              {
                path: "documents",
                name: "HRDocuments",
                component: () => import("@/components/hr/docs/index.vue"),
                beforeEnter: enforceHasCode,
              },
              {
                path: "onboarding",
                name: "HROnboarding",
                component: () => import("@/components/hr/onboarding/index.vue"),
                beforeEnter: enforceNoCode,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/accounting",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "AccountingEntry",
        beforeEnter: async () => {
          const companyStore = useCompanyStore();
          if (!companyStore.selected?.slug) {
            await companyStore.getData(true);
          }
          const slug = companyStore.selected?.slug;
          if (slug) {
            return { path: `/accounting/${encodeURIComponent(slug)}`, replace: true };
          }
          return { name: "NotFound" };
        },
      },
      {
        path: ":companySlug",
        name: "Accounting",
        meta: { companyScoped: true },
        component: () => import("@/pages/accounting.vue"),
      },
    ],
  },
  // {
  //   path: "/taxation-backup",
  //   component: () => import("@/layouts/base.vue"),
  //   meta: { requiresAuth: true },
  //   beforeEnter: () => {
  //     // if (!useAuthStore().hasTaxationAccess) {
  //     //   notify(
  //     //     {
  //     //       type: "error",
  //     //       title: "Access Control Failed",
  //     //       text: "You do not have permission to access taxation",
  //     //     },
  //     //     10000
  //     //   );
  //     //   return dashboardRoute();
  //     // }
  //   },
  //   children: [{ path: "", component: () => import("@/pages/taxation.vue") }],
  // },
  {
    path: "/taxation",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "TaxationEntry",
        beforeEnter: async () => {
          const companyStore = useCompanyStore();
          if (!companyStore.selected?.slug) {
            await companyStore.getData(true);
          }
          const slug = companyStore.selected?.slug;
          if (slug) {
            return { path: `/taxation/${encodeURIComponent(slug)}`, replace: true };
          }
          return { name: "NotFound" };
        },
      },
      {
        path: ":companySlug",
        name: "Taxation",
        meta: { companyScoped: true },
        component: () => import("@/pages/taxationv2.vue"),
      },
    ],
  },
  {
    path: "/salary-control/:id?",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true, withTitle: "Admin Tool" },
    beforeEnter: () => {
      if (!useSession().can.hrSalary) {
        notify(
          {
            type: "error",
            title: "Access Control Failed",
            text: "You do not have permission to access accounting",
          },
          10000
        );
        return dashboardRoute();
      }
    },
    children: [
      {
        path: "",
        name: "AdminSalaryControl",
        meta: { withTitle: "Salary Control" },
        component: () => import("@/pages/salarycontrol.vue"),
      },
    ],
  },
  {
    path: "/onboarding-control",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true, withTitle: "Admin Tool" },
    beforeEnter: () => {
      if (!useSession().account.value?.role?.canManageHumanResources) {
        notify(
          {
            type: "error",
            title: "Access Control Failed",
            text: "You do not have permission to access onboarding control",
          },
          10000
        );
        return dashboardRoute();
      }
    },
    children: [
      {
        path: "",
        name: "AdminOnboardingControl",
        meta: { withTitle: "adminNavigation.onboardingOverview" },
        component: () => import("@/pages/onboardingcontrol.vue"),
      },
    ],
  },
  {
    path: "/messages-control",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true, withTitle: "adminNavigation.messagesOverview" },
    beforeEnter: () => {
      if (!useSession().account.value?.role?.canManageHumanResources) {
        notify(
          {
            type: "error",
            title: "Access Control Failed",
            text: "You do not have permission to access messages overview",
          },
          10000
        );
        return dashboardRoute();
      }
    },
    children: [
      {
        path: "",
        name: "AdminMessagesControl",
        meta: { withTitle: "adminNavigation.messagesOverview" },
        component: () => import("@/pages/messagescontrol.vue"),
      },
    ],
  },
  {
    path: "/apsal",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true, withTitle: "adminNavigation.apsal" },
    beforeEnter: () => {
      if (!useSession().account.value?.role?.canManageHumanResources) {
        notify(
          {
            type: "error",
            title: "Access Control Failed",
            text: "You do not have permission to access Apsal",
          },
          10000
        );
        return dashboardRoute();
      }
    },
    children: [
      {
        path: "",
        name: "AdminApsal",
        meta: { withTitle: "adminNavigation.apsal" },
        component: () => import("@/pages/apsal.vue"),
      },
    ],
  },
  {
    path: "/login-statistics",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true, withTitle: "adminNavigation.loginStatistics" },
    beforeEnter: () => {
      if (!useSession().account.value?.role?.canAccessKeystone) {
        notify(
          {
            type: "error",
            title: "Access Control Failed",
            text: "You do not have permission to access login statistics",
          },
          10000
        );
        return dashboardRoute();
      }
    },
    children: [
      {
        path: "",
        name: "AdminLoginStatistics",
        meta: { withTitle: "adminNavigation.loginStatistics" },
        component: () => import("@/pages/loginstatistics.vue"),
      },
    ],
  },
  {
    path: "/impersonate",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true, withTitle: "Admin - Impersonation: Login as ..." },
    children: [
      {
        path: "",
        name: "Impersonation",
        component: () => import("@/components/admin/Impersonate.vue"),
      },
    ],
  },
  {
    path: "/inspect-user-id/:uid",
    meta: { requiresAuth: true, withTitle: "Please wait ..." },
    redirect: to => {
      return { path: `/inspect-admin/users/${to.params.uid}` }
    },
  },
  {
    path: "/inspect-admin/:name/:id?",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true, withTitle: "Admin Tool" },
    beforeEnter: () => {
      if (!useSession().account.value?.role?.canAccessAllCompanies) {
        notify(
          {
            type: "error",
            title: "Access Control Failed",
            text: "You do not have permission to use the user inspection tool",
          },
          10000
        );
        return dashboardRoute();
      }
    },
    children: [
      {
        path: "",
        name: "Redirection",
        meta: { withTitle: "Admin Tool" },
        component: () => import("@/pages/inspectadmin.vue"),
      },
    ],
  },
  {
    path: "/documents-review",
    component: () => import("@/layouts/base.vue"),
    meta: { requiresAuth: true, withTitle: "Admin Tool" },
    beforeEnter: () => {
      if (!useSession().account.value?.role?.canAccessAllCompanies) {
        notify(
          {
            type: "error",
            title: "Access Control Failed",
            text: "You do not have permission to access documents review",
          },
          10000
        );
        return dashboardRoute();
      }
      const { isHrEnabled } = useIsHrEnabled();
      if (!isHrEnabled.value) {
        notify(
          {
            type: "error",
            title: "Error",
            text: "Cannot access this page. It may not exist.",
          },
          10000
        );
        return dashboardRoute();
      }
    },
    children: [
      {
        path: "",
        name: "AdminDocumentsReview",
        meta: { withTitle: "adminNavigation.documentsReview" },
        component: () => import("@/pages/documentsreview.vue"),
      },
    ],
  },
  {
    path: "/my-profile",
    meta: { requiresAuth: true },
    component: () => import("@/layouts/base.vue"),
    children: [
      {
        path: "",
        name: "MyProfile",
        component: () => import("@/pages/myprofile.vue"),
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/pages/notfound.vue"),
  },
  {
    path: "/maintenance",
    name: "Maintenance",
    component: () => import("@/pages/maintenance.vue"),
    children: [
      {
        path: "2",
        component: () => import("@/pages/maintenance.vue"),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from) => {

  // Maintenance mode
  const maintenanceRouteName = "Maintenance";
  if (isOnline !== "yes" && to.name !== maintenanceRouteName) {
    return { name: maintenanceRouteName };
  }

  // Navigate back to home if site is back online
  if (isOnline === "yes" && to.name === maintenanceRouteName) {
    return { name: "Home" };
  }

  if (to.meta.requiresAuth) {
    const { code } = await useAuthStore().checkauth();
    if (code === "ERROR") {
      const navStore = await useNavStore();
      const toSave = { name: to.name, params: to.params, query: to.query };
      navStore.setPreviousRoute(toSave); // Store the current route
      return { name: "RequestSignIn" };
    }
  }

  if (to.params.companySlug) {
    const syncResult = await syncRouteContext(to);
    if (syncResult !== true) {
      return syncResult;
    }
  }
});

export default router;
